import { useParams, useSearchParams } from '@direct-frontend/modules-router';
import { Form } from '@adv-frontend/form-kit';
import { useGetDigitalOrderQuery } from '@/entities/digitalOrder';
import { isOrderEditable } from '@/entities/order';
import { AccountAvatar } from '@/features/AccountAvatar';
import { CheckoutLogo } from '@/features/CheckoutLogo';
import {
    DIGITAL_ORDER_FORM_ID,
    DigitalOrderForm,
    digitalOrderForm,
    OrderFormDigitalPaymentInformer,
    toDigitalFormValues,
} from '@/features/OrderForm';
import { DEFAULT_DIGITAL_ORDER } from '@/shared/api/digitalOrder';
import { type SchemaDigitalOrder } from '@/shared/api/openApi';
import { Footer } from '@/shared/components/Footer';
import { Layout } from '@/shared/components/Layout';
import { buildShoppingCartUrl } from '@/shared/lib/buildShoppingCartUrl';
import { useInitialRender } from '@/shared/lib/hooks';
import { getRequestContext } from '@/shared/lib/requestContext';
import { DigitalCheckoutFeedback } from '@/widgets/DigitalCheckoutFeedback';
import { DigitalCheckoutModals } from '../DigitalCheckoutModals';
import { OrderNotFound } from '../OrderNotFound';
import {
    getDigitalCheckoutFormContext,
    useDigitalCheckoutModals,
    useDigitalPayboxSubmit,
    useTrackDigitalCheckout,
} from './lib';
import styles from './DigitalCheckout.module.css';

interface DigitalCheckoutContentProps {
    order: SchemaDigitalOrder;
    signature: string;
    storeSlug: string;
    refetch: () => Promise<unknown>;
}

const DigitalCheckoutContent = ({ order, signature, storeSlug, refetch }: DigitalCheckoutContentProps) => {
    const { company } = getRequestContext();
    const { companyShopUrl } = company;

    const { modals } = useDigitalCheckoutModals();

    const formContext = getDigitalCheckoutFormContext({
        orderId: order.id,
        signature,
        storeSlug,
        companyShopUrl: companyShopUrl || '',
        order,
        refetch,
    });

    const { handlePayboxSubmit, payboxUrl } = useDigitalPayboxSubmit({
        orderId: order.id,
        signature,
    });

    return (
        <Form
            id={DIGITAL_ORDER_FORM_ID}
            form={digitalOrderForm}
            initialValue={toDigitalFormValues(order)}
            context={formContext}
        >
            <Layout
                accountSlot={<AccountAvatar />}
                logoSlot={<CheckoutLogo />}
                companyShopUrl={buildShoppingCartUrl(company)}
                sidebarSlot={<OrderFormDigitalPaymentInformer order={order} handlePayboxSubmit={handlePayboxSubmit} />}
                footerSlot={<Footer hideOnDesktop centered className={styles.footer} />}
            >
                <DigitalOrderForm order={order} items={order.items ?? []} />
            </Layout>
            <DigitalCheckoutModals
                order={order}
                signature={signature}
                storeSlug={storeSlug}
                modals={modals}
                payboxUrl={payboxUrl}
            />
        </Form>
    );
};

export const DigitalCheckout = () => {
    const { orderId = '', storeSlug = '' } = useParams();
    const { company, signature: requestContextSignature } = getRequestContext();
    const [searchParams] = useSearchParams();
    const signature = searchParams.get('signature') || requestContextSignature || '';

    const {
        data: order,
        isFetching,
        refetch,
    } = useGetDigitalOrderQuery({ order_id: orderId, signature, store_slug: storeSlug });

    const isMounted = useInitialRender();

    useTrackDigitalCheckout();

    if ((!isFetching && !order) || !signature) {
        return <OrderNotFound />;
    }

    if (!isMounted || !order) {
        return null;
    }

    if (order && !isOrderEditable(order)) {
        return (
            <Layout
                accountSlot={<AccountAvatar />}
                logoSlot={<CheckoutLogo />}
                companyShopUrl={buildShoppingCartUrl(company)}
            >
                <DigitalCheckoutFeedback order={order} />
            </Layout>
        );
    }

    return (
        <DigitalCheckoutContent
            order={order || DEFAULT_DIGITAL_ORDER}
            signature={signature}
            storeSlug={storeSlug}
            refetch={refetch}
        />
    );
};
